import React, { useMemo, useState } from 'react';
import {
  DateUtil,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalSection,
  Button,
  Form,
  Grid,
  GridRow,
  GridColumn,
  Input,
  Dropdown,
  useApi,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateLanguage,
  CandidateRedux,
} from '@axiom/validation';
import { CandidateProfileStatusStateMachine } from '@axiom/utils';
import {
  CandidatesConst,
  CandidateSettingsConst,
  TaxonomyConst,
} from '@axiom/const';

import { BarredLocationApi } from '../../api/barred-location-api';
import { LegacyUserApi } from '../../api/legacy-user-api';
import { TalentCollaboratorsLegacyApi } from '../../api/talent-collaborators-legacy-api';
import { LanguagesApi } from '../../api/languages-api';
import HorizontalRule from '../HorizontalRule/HorizontalRule';
import { TalentOpportunitiesApi } from '../../api/talent-opportunities-api';
import { TalentProfilePendingAlumModal } from '../TalentProfilePendingAlumModal/TalentProfilePendingAlumModal';
import { CandidateApi } from '../../api/candidate-api';
import { TalentLanguagesApi } from '../../api/talent-languages-api';
import { TalentProfileEditFormSchema } from '../../schema/talent-profile-edit-form-schema';
import { useRefreshCandidate } from '../../hooks/useRefreshCandidate';

import { TalentProfileEditFormLanguages } from './TalentProfileEditFormLanguages';

const maxYearsRange = 75;
const {
  ProfileStatuses: { PendingAlum, PendingAlumDNR },
} = CandidatesConst;

const QUALIFYING_UK_ADMISSION_NAMES = new Set<string>(
  CandidateSettingsConst.QualifyingUkAdmissionNames
);

export interface TalentProfileEditFormProps {
  candidate: CandidateRedux | Candidate;
  name: string;
  onClose: () => void;
}

export const TalentProfileEditForm = ({
  onClose,
  candidate,
  name,
}: TalentProfileEditFormProps) => {
  const { refreshCandidate } = useRefreshCandidate(candidate.id);
  const [
    { data: barredLocations },
    { data: users },
    { data: preselectedCollaborators },
    { data: languagesData },
    { data: opportunityCandidatesByTalent },
  ] = useApi(
    BarredLocationApi.readBarredLocations(),
    LegacyUserApi.readUsers(),
    TalentCollaboratorsLegacyApi.readCollaborators(candidate.id),
    LanguagesApi.readLanguages(),
    TalentOpportunitiesApi.readCandidateOpportunities(candidate.id)
  );
  const [showPendingAlumModal, setShowPendingAlumModal] = useState(false);
  const [changedProfileStatus, setChangedProfileStatus] = useState('');
  const candidateProfileStatusTransition =
    new CandidateProfileStatusStateMachine(
      candidate,
      opportunityCandidatesByTalent
    );

  const statuses =
    candidateProfileStatusTransition.allowedEndUserStatusTransitions();

  const practicingYearsRange = useMemo(() => {
    const data = [
      {
        label: '<1',
        value: 0,
      },
    ];

    for (let i = 1; i <= maxYearsRange; i += 1) {
      data.push({
        label: `${i}`,
        value: i,
      });
    }

    return data;
  }, []);
  return (
    <Form
      name={name}
      schema={TalentProfileEditFormSchema}
      onSubmit={async (changedData, _, formData) => {
        const { languages, collaborators, lawyerTypes, ...talentPatchObject } =
          changedData;
        if (
          typeof talentPatchObject.practiceStartYear === 'number' &&
          talentPatchObject.practiceStartYear >= 0
        ) {
          talentPatchObject.practiceStartYear = DateUtil.yearsCountToYear(
            talentPatchObject.practiceStartYear
          );
        }

        if (lawyerTypes !== undefined) {
          (talentPatchObject as Partial<Candidate>).settings = {
            lawyerSpecificType:
              lawyerTypes && lawyerTypes.length > 0
                ? [{ countryCode: 'UK', type: lawyerTypes }]
                : [],
          };
        }

        if (Object.keys(talentPatchObject).length > 0) {
          await CandidateApi.updateCandidate(candidate.id, {
            ...talentPatchObject,
          } as Candidate);
        }
        if (languages && Object.keys(languages).length > 0) {
          await TalentLanguagesApi.updateLanguages(candidate.id, languages);
          await TalentLanguagesApi.refreshLanguages(candidate.id);
        }
        if (collaborators) {
          await TalentCollaboratorsLegacyApi.updateCollaborators(
            candidate.id,
            collaborators
          );
          await TalentCollaboratorsLegacyApi.refreshCollaborators(candidate.id);
        }
        await refreshCandidate(candidate.id);
        onClose();
      }}
      initialValues={{
        profileStatus: candidate.profileStatus,
        displayFirstName: candidate.displayFirstName,
        displayLastName: candidate.displayLastName,
        barredLocations: candidate.barredLocations?.map(bl => bl.id),
        lawyerTypes:
          (candidate.settings?.lawyerSpecificType ?? []).find(
            entry => entry.countryCode === 'UK'
          )?.type ?? [],
        noticePeriod: candidate.noticePeriod,
        practiceStartYear: DateUtil.yearToYearsCount(
          candidate.practiceStartYear
        ),
        languages: candidate.languages ?? [],
        collaborators: preselectedCollaborators.map(c => c.id),
      }}
    >
      {({ fireSubmit, values, setValues }) => {
        const selectedBarredLocationNames = (values.barredLocations ?? [])
          .map(id => barredLocations.find(bl => bl.id === id)?.name)
          .filter(Boolean);
        const qualifiesForUkLawyerType =
          candidate.businessTitle === TaxonomyConst.LawyerBusinessTitle &&
          selectedBarredLocationNames.some(blName =>
            QUALIFYING_UK_ADMISSION_NAMES.has(blName)
          );
        const hasExistingUkLawyerType = (
          candidate.settings?.lawyerSpecificType ?? []
        ).some(entry => entry.countryCode === 'UK' && entry.type.length > 0);
        const showUkLawyerType =
          qualifiesForUkLawyerType || hasExistingUkLawyerType;
        return (
          <>
            <Modal name="edit-talent-info">
              <ModalHeader
                onClose={() => {
                  onClose();
                }}
              >
                Edit Talent Info
              </ModalHeader>
              <ModalSection>
                <Grid>
                  <GridRow>
                    <GridColumn>
                      <Dropdown
                        onChange={value => {
                          setChangedProfileStatus(value as string);
                        }}
                        label="Profile Status"
                        name="profileStatus"
                        options={statuses.map((status: string) => ({
                          label: status,
                          value: status,
                        }))}
                        placeholder={candidate.profileStatus}
                        displayKey="label"
                        valueKey="value"
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <HorizontalRule />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn width={6}>
                      <Input
                        label="Display First Name"
                        name="displayFirstName"
                        placeholder={candidate.firstName ?? ''}
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Input
                        label="Display Last Name"
                        name="displayLastName"
                        placeholder={candidate.lastName ?? ''}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <HorizontalRule />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <Dropdown
                        label="Bar Admission"
                        name="barredLocations"
                        options={barredLocations.map(bl => ({
                          label: bl.name,
                          value: bl.id,
                        }))}
                        displayKey="label"
                        valueKey="value"
                      />
                    </GridColumn>
                  </GridRow>
                  {showUkLawyerType ? (
                    <>
                      <GridRow>
                        <GridColumn>
                          <HorizontalRule />
                        </GridColumn>
                      </GridRow>
                      <GridRow>
                        <GridColumn>
                          <Dropdown
                            label="UK Lawyer Type"
                            name="lawyerTypes"
                            options={Object.values(
                              CandidateSettingsConst.UkLawyerType
                            ).map(lawyerType => ({
                              label: lawyerType,
                              value: lawyerType,
                            }))}
                            displayKey="label"
                            valueKey="value"
                          />
                        </GridColumn>
                      </GridRow>
                    </>
                  ) : null}
                  <GridRow>
                    <GridColumn>
                      <HorizontalRule />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn width={6}>
                      <Input name="noticePeriod" label="Notice Period" />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Dropdown
                        label="Years Practicing"
                        options={practicingYearsRange}
                        name="practiceStartYear"
                        displayKey="label"
                        valueKey="value"
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <HorizontalRule />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <TalentProfileEditFormLanguages
                        name="TALENT_PROFILE_EDIT_FORM_LANGUAGES"
                        languages={languagesData}
                        candidate={candidate}
                        fields={values.languages}
                        addParentFormLanguageValue={async (
                          newLanguage: Partial<CandidateLanguage>
                        ) => {
                          const newLanguages = values.languages ?? [];
                          newLanguages.push(newLanguage as CandidateLanguage);
                          await setValues({
                            ...values,
                            languages: newLanguages,
                          });
                        }}
                        removeParentFormLanguageValue={async index => {
                          const x = values.languages ?? [];
                          let newLanguages = [...x];
                          newLanguages = newLanguages.filter(
                            (_, i) => i !== index
                          );
                          await setValues({
                            ...values,
                            languages: newLanguages,
                          });
                        }}
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <HorizontalRule />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <Dropdown
                        label="Talent Collaborators"
                        options={users.map(user => ({
                          label: user.fullName,
                          value: user.id,
                        }))}
                        displayKey="label"
                        name="collaborators"
                        valueKey="value"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
              </ModalSection>
              <ModalFooter>
                <Button
                  name="EDITFORMCANCELBUTTON"
                  onClick={() => {
                    onClose();
                  }}
                  variation="outline"
                >
                  Cancel
                </Button>
                <Button
                  name="EDITCANDIDATESAVEBUTTON"
                  onClick={async () => {
                    if (
                      changedProfileStatus === PendingAlum ||
                      changedProfileStatus === PendingAlumDNR
                    ) {
                      setShowPendingAlumModal(true);
                    } else {
                      await fireSubmit();
                    }
                  }}
                >
                  Save
                </Button>
              </ModalFooter>
            </Modal>

            {showPendingAlumModal ? (
              <TalentProfilePendingAlumModal
                profileStatus={values.profileStatus ?? 'Alum'}
                onCancel={() => {
                  setShowPendingAlumModal(false);
                }}
                onSave={fireSubmit}
              />
            ) : null}
          </>
        );
      }}
    </Form>
  );
};
